/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hr` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HrMesg : Mesg {
    public constructor() : super("Hr", Profile.MesgNum.HR)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** fractional_timestamp, in s. */
    public var fractionalTimestamp: Double?
        get() = getFieldValue(FRACTIONAL_TIMESTAMP_FIELD_NUM) as? Double
        set(value) = setFieldValue(FRACTIONAL_TIMESTAMP_FIELD_NUM, value)

    /** time256, in s. */
    public var time256: Double?
        get() = getFieldValue(TIME256_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME256_FIELD_NUM, value)

    /** Number of values stored in filtered_bpm. */
    public val numFilteredBpm: Int
        get() = getField(FILTERED_BPM_FIELD_NUM)?.numValues ?: 0

    /** filtered_bpm, in bpm. */
    public var filteredBpm: List<UByte?>?
        get() {
            val field = getField(FILTERED_BPM_FIELD_NUM) ?: return null
            return List(field.numValues) { getFilteredBpm(it) }
        }
        set(value) {
            removeField(FILTERED_BPM_FIELD_NUM)
            value?.forEachIndexed { index, element -> setFilteredBpm(index, element) }
        }

    public fun getFilteredBpm(index: Int): UByte? =
        getFieldValue(FILTERED_BPM_FIELD_NUM, index) as? UByte

    public fun setFilteredBpm(index: Int, value: UByte?) {
        setFieldValue(FILTERED_BPM_FIELD_NUM, value, index)
    }

    /** Number of values stored in event_timestamp. */
    public val numEventTimestamp: Int
        get() = getField(EVENT_TIMESTAMP_FIELD_NUM)?.numValues ?: 0

    /** event_timestamp, in s. */
    public var eventTimestamp: List<Double?>?
        get() {
            val field = getField(EVENT_TIMESTAMP_FIELD_NUM) ?: return null
            return List(field.numValues) { getEventTimestamp(it) }
        }
        set(value) {
            removeField(EVENT_TIMESTAMP_FIELD_NUM)
            value?.forEachIndexed { index, element -> setEventTimestamp(index, element) }
        }

    public fun getEventTimestamp(index: Int): Double? =
        getFieldValue(EVENT_TIMESTAMP_FIELD_NUM, index) as? Double

    public fun setEventTimestamp(index: Int, value: Double?) {
        setFieldValue(EVENT_TIMESTAMP_FIELD_NUM, value, index)
    }

    /** Number of values stored in event_timestamp_12. */
    public val numEventTimestamp12: Int
        get() = getField(EVENT_TIMESTAMP_12_FIELD_NUM)?.numValues ?: 0

    /** event_timestamp_12. */
    public var eventTimestamp12: List<UByte?>?
        get() {
            val field = getField(EVENT_TIMESTAMP_12_FIELD_NUM) ?: return null
            return List(field.numValues) { getEventTimestamp12(it) }
        }
        set(value) {
            removeField(EVENT_TIMESTAMP_12_FIELD_NUM)
            value?.forEachIndexed { index, element -> setEventTimestamp12(index, element) }
        }

    public fun getEventTimestamp12(index: Int): UByte? =
        getFieldValue(EVENT_TIMESTAMP_12_FIELD_NUM, index) as? UByte

    public fun setEventTimestamp12(index: Int, value: UByte?) {
        setFieldValue(EVENT_TIMESTAMP_12_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val FRACTIONAL_TIMESTAMP_FIELD_NUM: UByte = 0u
        public const val TIME256_FIELD_NUM: UByte = 1u
        public const val FILTERED_BPM_FIELD_NUM: UByte = 6u
        public const val EVENT_TIMESTAMP_FIELD_NUM: UByte = 9u
        public const val EVENT_TIMESTAMP_12_FIELD_NUM: UByte = 10u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("FractionalTimestamp", 0u, BaseType.UINT16, 32768.0, 0.0, "s", false)
            fields[1] = Field("Time256", 1u, BaseType.UINT8, 256.0, 0.0, "s", false)
                .addComponent(FieldComponent(0u, false, 8, 256.0, 0.0))
            fields[6] = Field("FilteredBpm", 6u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[9] = Field("EventTimestamp", 9u, BaseType.UINT32, 1024.0, 0.0, "s", true)
            fields[10] = Field("EventTimestamp12", 10u, BaseType.BYTE, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
                .addComponent(FieldComponent(9u, true, 12, 1024.0, 0.0))
            return fields
        }
    }
}
